import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    Button,
    Alert,
    MenuItem,
} from "@mui/material";
import { motion } from "framer-motion";
import SwanLogo from "../../assets/logos/swansolutions_logo.png";
import FigLogo from "../../assets/logos/fig_CROP.png";
import SwanAiLogo from "../../assets/logos/swan_ai_labs_logo.png";

const careerPaths = [
    {
        type: "Sales & Marketing",
        description: `Our Sales and Marketing team is at the forefront of helping K12 schools and partners discover Swan Solutions' impact. We value professionals who are passionate about education, eager to learn, and skilled in building relationships that last. If you thrive on communicating value, exploring new markets, and supporting growth, we want to hear from you!`,
        icon: FigLogo,
        position: "Sales/Marketing",
    },
    {
        type: "Technical",
        description: `We're always seeking Full Stack Developers, Data Scientists, and AI Developers who want to make a difference in K12 education technology. Our technical team is dedicated to building scalable, secure, and user-friendly solutions. If you love clean code, practical problem-solving, and are always learning new tools, this is the place to be.`,
        icon: SwanAiLogo,
        position: "Technical",
    },
];

const positionOptions = [
    { value: "Sales/Marketing", label: "Sales & Marketing" },
    { value: "Full Stack Developer", label: "Full Stack Developer" },
    { value: "Data Scientist", label: "Data Scientist" },
    { value: "AI Developer", label: "AI Developer" },
];

const initialForm = {
    FirstName: "",
    LastName: "",
    Email: "",
    Phone: "",
    AboutMe: "",
    Position: "",
    Resume: null,
};

export default function Careers() {
    const [form, setForm] = useState(initialForm);
    const [status, setStatus] = useState({ loading: false, error: null, success: null });
    const [resumeFileName, setResumeFileName] = useState("");

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "Resume") {
            setForm({ ...form, Resume: files[0] });
            setResumeFileName(files[0]?.name || "");
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    const validate = () => {
        if (
            !form.FirstName.trim() ||
            !form.LastName.trim() ||
            !form.Email.trim() ||
            !form.Phone.trim() ||
            !form.Position.trim() ||
            !form.AboutMe.trim() ||
            !form.Resume
        ) return false;
        if (!/\S+@\S+\.\S+/.test(form.Email)) return false;
        if (!/\d{7,}/.test(form.Phone)) return false;
        return true;
    };

    const buildHtml = (resumeUrl) => `
    <div style="font-family:Segoe UI,Arial,sans-serif;max-width:600px;margin:0 auto;">
      <h2 style="background:#21dbb8;padding:18px 0;color:#054067;text-align:center;margin-top:0;">
        New Career Application - Swan Solutions
      </h2>
      <table style="width:100%;font-size:1rem;border-collapse:collapse;">
        <tr><td style="font-weight:bold;padding:8px;">First Name:</td><td>${form.FirstName}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Last Name:</td><td>${form.LastName}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Email:</td><td>${form.Email}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Phone:</td><td>${form.Phone}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Position:</td><td>${form.Position}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">About Me:</td><td>${form.AboutMe.replace(/\n/g, "<br/>")}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Resume:</td><td><a href="${resumeUrl}" target="_blank">${resumeUrl}</a></td></tr>
      </table>
      <hr style="margin:30px 0 12px 0;">
      <div style="color:#7e8b99;font-size:0.96rem;">Career form submitted from <b>swansolutions.com</b></div>
    </div>
  `;

    const buildText = (resumeUrl) => `
New Career Application - Swan Solutions

First Name: ${form.FirstName}
Last Name: ${form.LastName}
Email: ${form.Email}
Phone: ${form.Phone}
Position: ${form.Position}
About Me:
${form.AboutMe}

Resume: ${resumeUrl}

  `;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, error: null, success: null });

        if (!validate()) {
            setStatus({ loading: false, error: "Please fill all required fields and attach your resume.", success: null });
            return;
        }

        // 1. Upload the resume file
        let resumeUrl = "";
        try {
            const fileData = new FormData();
            fileData.append("file", form.Resume, form.Resume.name);
            fileData.append("folder", "resume");

            const fileRes = await fetch("https://media.swancreate.com/upload", {
                method: "POST",
                body: fileData,
            });

            const fileJson = await fileRes.json();
            // Extract filename from: "File 'fig_Finance.pdf' uploaded successfully"
            let uploadedFileName = "";
            if (
                fileJson &&
                typeof fileJson.message === "string"
            ) {
                const match = fileJson.message.match(/'([^']+)'/);
                if (match && match[1]) {
                    uploadedFileName = match[1];
                }
            }

            if (uploadedFileName && fileRes.ok) {
                resumeUrl = `https://media.swancreate.com/images/resumes/${uploadedFileName}`;
            } else {
                throw new Error("File upload failed");
            }
        } catch (err) {
            setStatus({ loading: false, error: "Resume upload failed. Please try again.", success: null });
            return;
        }

        // 2. Post to the SQL API (do not send apiid)
        try {
            const dbRes = await fetch("https://figazuresql.swancreate.com/api/crm/CareerPage", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    FirstName: form.FirstName,
                    LastName: form.LastName,
                    Email: form.Email,
                    Phone: form.Phone,
                    AboutMe: form.AboutMe,
                    Position: form.Position,
                }),
            });
            if (!dbRes.ok) throw new Error("DB error");
        } catch {
            setStatus({ loading: false, error: "Error saving your application. Please try again.", success: null });
            return;
        }

        // 3. Send the notification email to both addresses
        try {
            const emailRes = await fetch("https://mailgun.swancreate.com/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    to: "dusty@swank12.com,becki@figk-12.com",
                    subject: "New Career Application — Swan Solutions Website",
                    html: buildHtml(resumeUrl),
                    text: buildText(resumeUrl),
                }),
            });
            if (!emailRes.ok) throw new Error("Email error");
        } catch {
            setStatus({ loading: false, error: "Unable to send email notification. Your application was received.", success: null });
            return;
        }

        setStatus({ loading: false, error: null, success: "Thank you! Your application has been submitted." });
        setForm(initialForm);
        setResumeFileName("");
    };

    return (
        <Box sx={{ bgcolor: "#f7fafb", minHeight: "100vh" }}>
            {/* HERO */}
            <Box
                component={motion.section}
                initial={{ opacity: 0, y: -64 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                sx={{
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    px: 0,
                    py: { xs: 10, md: 14 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background: "linear-gradient(120deg, #0b223e 0%, #2269a6 45%, #21dbb8 100%)",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    minHeight: { xs: "30vh", md: "36vh" },
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 6px 40px 0 rgba(30,90,150,0.13)",
                    mb: 8,
                }}
            >
                <Box
                    component="img"
                    src={SwanLogo}
                    alt="Swan Solutions Logo"
                    sx={{
                        height: { xs: 70, md: 110 },
                        mb: 3,
                        filter: "drop-shadow(0 4px 24px rgba(0,0,0,0.12))",
                    }}
                />
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 900,
                        mb: 2,
                        fontSize: { xs: "2rem", md: "3rem" },
                        letterSpacing: "-0.04em",
                        textShadow: "0 3px 16px rgba(25,60,110,0.15)",
                    }}
                >
                    Join Swan Solutions
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        maxWidth: 800,
                        mx: "auto",
                        mb: 3,
                        color: "rgba(255,255,255,0.97)",
                        fontWeight: 400,
                        fontSize: { xs: "1.1rem", md: "1.2rem" },
                        textShadow: "0 2px 12px rgba(40,120,140,0.08)",
                    }}
                >
                    We are always on the lookout for the best talent. Swan Solutions is committed to continuous improvement and growth in everything we do. Whether you're an expert in Sales & Marketing or a Technical leader in Full Stack Development, AI, or Data Science, your work will help shape the future of K12 technology and impact districts nationwide.
                </Typography>
            </Box>

            {/* Career Paths */}
            <Container maxWidth="md" sx={{ py: 6 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 4, textAlign: "center" }}>
                    Career Paths at Swan Solutions
                </Typography>
                <Grid container spacing={4} justifyContent="center">
                    {careerPaths.map((path, idx) => (
                        <Grid item xs={12} md={6} key={path.type}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                                whileHover={{ scale: 1.03 }}
                            >
                                <Card sx={{
                                    borderRadius: 4,
                                    boxShadow: 4,
                                    py: 3,
                                    px: 2,
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                }}>
                                    {/* Logo on top */}
                                    <Box
                                        component="img"
                                        src={path.icon}
                                        alt={path.type + " Logo"}
                                        sx={{
                                            height: 54,
                                            width: "auto",
                                            mb: 2,
                                            mt: 1,
                                            filter: idx === 0 ? undefined : "drop-shadow(0 4px 16px #21dbb8cc)",
                                        }}
                                    />
                                    <CardContent>
                                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, textAlign: "center" }}>
                                            {path.type}
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: "text.secondary", textAlign: "center" }}>
                                            {path.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Application Form */}
            <Container maxWidth="sm" sx={{ bgcolor: "#fff", py: 6, borderRadius: 5, boxShadow: 3, mb: 12 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: "center" }}>
                    Submit Your Application
                </Typography>
                <form onSubmit={handleSubmit} autoComplete="off" encType="multipart/form-data">
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                name="FirstName"
                                label="First Name"
                                value={form.FirstName}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                name="LastName"
                                label="Last Name"
                                value={form.LastName}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                name="Email"
                                label="Email"
                                type="email"
                                value={form.Email}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                name="Phone"
                                label="Phone"
                                value={form.Phone}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                select
                                required
                                name="Position"
                                label="Position"
                                value={form.Position}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                            >
                                <MenuItem value="">Select a path</MenuItem>
                                {positionOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                name="AboutMe"
                                label="Tell us about yourself"
                                value={form.AboutMe}
                                onChange={handleChange}
                                multiline
                                minRows={5}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="outlined"
                                component="label"
                                sx={{ mb: 1, borderRadius: 2, fontWeight: 600, fontSize: "1.09rem" }}
                            >
                                {resumeFileName ? `Resume: ${resumeFileName}` : "Attach Resume (PDF, DOC, DOCX, etc.)"}
                                <input
                                    name="Resume"
                                    type="file"
                                    accept=".pdf,.doc,.docx,.txt,.rtf"
                                    onChange={handleChange}
                                    hidden
                                    required
                                />
                            </Button>
                            <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                                (Required)
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 2 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    component={motion.button}
                                    whileTap={{ scale: 0.96 }}
                                    whileHover={{ scale: 1.03 }}
                                    sx={{
                                        borderRadius: 99,
                                        px: 5,
                                        py: 1.3,
                                        fontWeight: 700,
                                        fontSize: "1.15rem",
                                        background: "linear-gradient(90deg, #145c8c, #21dbb8)",
                                    }}
                                    disabled={status.loading}
                                >
                                    {status.loading ? "Submitting..." : "Submit Application"}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
                <Box sx={{ mt: 3 }}>
                    {status.success && <Alert severity="success">{status.success}</Alert>}
                    {status.error && <Alert severity="error">{status.error}</Alert>}
                </Box>
            </Container>
        </Box>
    );
}
